import React, { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { Car, Truck, Gem, Mountain, Zap, ChevronDown, Gauge, Caravan, Container } from "lucide-react";
import { api } from "../lib/api";
import VehicleCard from "../components/VehicleCard";

const CAT_ICON = { osobni: Car, dodavky: Truck, luxusni: Gem, suv: Mountain, sportovni: Gauge, obytne: Caravan, voziky: Container, elektro: Zap };
const FUELS = ["Benzín", "Nafta", "Elektro", "Hybrid"];
const GEARS = ["Manuál", "Automat"];

export default function Fleet() {
  const [params, setParams] = useSearchParams();
  const activeCat = params.get("kategorie") || "osobni";
  const [cats, setCats] = useState([]);
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fuels, setFuels] = useState([]);
  const [gears, setGears] = useState([]);
  const [sort, setSort] = useState("newest");

  useEffect(() => {
    api.get("/categories").then((r) => setCats(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    api
      .get("/cars", { params: { category: activeCat } })
      .then((r) => setCars(r.data))
      .catch(() => setCars([]))
      .finally(() => setLoading(false));
  }, [activeCat]);

  const setCat = (slug) => setParams({ kategorie: slug });
  const toggle = (arr, setArr, val) =>
    setArr(arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val]);

  const filtered = useMemo(() => {
    let list = [...cars];
    if (fuels.length) list = list.filter((c) => fuels.includes(c.fuel));
    if (gears.length) list = list.filter((c) => gears.includes(c.transmission));
    if (sort === "price_asc") list.sort((a, b) => a.price_per_day - b.price_per_day);
    else if (sort === "price_desc") list.sort((a, b) => b.price_per_day - a.price_per_day);
    return list;
  }, [cars, fuels, gears, sort]);

  const activeCatData = cats.find((c) => c.slug === activeCat);

  return (
    <div className="bg-black" data-testid="fleet-page">
      {/* category selector */}
      <section className="container-kame pt-14 pb-8">
        <p className="eyebrow">Vyberte kategorii</p>
        <h1 className="font-display text-4xl sm:text-5xl text-white uppercase mt-2 mb-8">Kategorie vozů</h1>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {cats.map((c) => {
            const Icon = CAT_ICON[c.slug] || Car;
            const isActive = c.slug === activeCat;
            return (
              <button
                key={c.slug}
                onClick={() => setCat(c.slug)}
                data-testid={`category-btn-${c.slug}`}
                className={`p-5 rounded-sm border text-center transition-colors duration-200 ${
                  isActive
                    ? "border-[#B58A50] bg-[#B58A50]/10"
                    : "border-[#1f1f1f] bg-[#0d0d0d] hover:border-[#B58A50]/50"
                }`}
              >
                <Icon className={`mx-auto mb-3 ${isActive ? "text-[#B58A50]" : "text-white/70"}`} size={30} />
                <p className="font-display text-white uppercase text-sm">{c.name}</p>
                <p className="text-[#B58A50] text-xs mt-1">od {c.price_from.toLocaleString("cs-CZ")} Kč / den</p>
              </button>
            );
          })}
        </div>
      </section>

      {/* content */}
      <section className="container-kame pb-20 grid lg:grid-cols-[260px_1fr] gap-8">
        {/* filters */}
        <aside className="card-kame p-6 h-fit lg:sticky lg:top-24" data-testid="fleet-filters">
          <h3 className="eyebrow mb-5">Filtrujte vozy</h3>

          <div className="mb-6">
            <p className="text-white font-semibold text-sm uppercase mb-3">Palivo</p>
            <div className="space-y-2">
              {FUELS.map((f) => (
                <label key={f} className="flex items-center gap-2 text-sm text-white/70 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={fuels.includes(f)}
                    onChange={() => toggle(fuels, setFuels, f)}
                    className="accent-[#B58A50] w-4 h-4"
                    data-testid={`filter-fuel-${f}`}
                  />
                  {f}
                </label>
              ))}
            </div>
          </div>

          <div className="mb-6">
            <p className="text-white font-semibold text-sm uppercase mb-3">Převodovka</p>
            <div className="space-y-2">
              {GEARS.map((g) => (
                <label key={g} className="flex items-center gap-2 text-sm text-white/70 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={gears.includes(g)}
                    onChange={() => toggle(gears, setGears, g)}
                    className="accent-[#B58A50] w-4 h-4"
                    data-testid={`filter-gear-${g}`}
                  />
                  {g}
                </label>
              ))}
            </div>
          </div>

          {(fuels.length > 0 || gears.length > 0) && (
            <button
              onClick={() => { setFuels([]); setGears([]); }}
              className="text-[#B58A50] text-xs uppercase tracking-wider hover:underline"
              data-testid="filter-reset"
            >
              Zrušit filtry
            </button>
          )}
        </aside>

        {/* grid */}
        <div>
          <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
            <div>
              <h2 className="font-display text-3xl text-white uppercase">{activeCatData?.name || "Vozy"}</h2>
              <p className="text-white/50 text-sm">{filtered.length} vozů</p>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-white/50 text-xs uppercase tracking-wider">Seřadit</span>
              <div className="relative">
                <select
                  value={sort}
                  onChange={(e) => setSort(e.target.value)}
                  data-testid="fleet-sort"
                  className="appearance-none bg-[#0d0d0d] border border-[#1f1f1f] text-white text-sm rounded-sm pl-3 pr-9 py-2 focus:border-[#B58A50] outline-none"
                >
                  <option value="newest">Nejnovější</option>
                  <option value="price_asc">Cena vzestupně</option>
                  <option value="price_desc">Cena sestupně</option>
                </select>
                <ChevronDown size={16} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-white/50 pointer-events-none" />
              </div>
            </div>
          </div>

          {loading ? (
            <p className="text-white/50">Načítám vozy…</p>
          ) : filtered.length === 0 ? (
            <div className="card-kame p-10 text-center text-white/60" data-testid="fleet-empty">
              V této kategorii aktuálně nejsou žádné vozy odpovídající filtrům.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
              {filtered.map((car, i) => (
                <VehicleCard key={car.id} car={car} index={i} />
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
